import type { HttpErrorResponse } from '@angular/common/http';

import { createSelector } from '@ngrx/store';

import { AbilitiesSelectors } from '@core/abilities-data';
import type {
  AdjustSettingsSubtasks,
  CreateCareersSiteSubtasks,
  GetStartedSubtasks,
  ManageCandidatesSubtasks,
  PostYourFirstJobSubtasks,
  VisitOurMarketplaceSubtasks,
  GettingStartedStep,
  GettingStartedSubtask,
  GettingStartedSubtaskType,
  FirstUnfinishedStepAndSubtask,
  ViewJobsSubtasks,
  ComposeYourMessagingSubtasks,
  GettingStartedConfigWithContent,
  GettingStartedContentResponse,
} from '@core/getting-started-types';
import { createSingleEntityStore, ngrxEvent, pipeState } from '@core/ngrx-legacy';

import {
  findStepForSubtask,
  firstUnfinishedSubtask,
  getAvailableSubtasksVisitedState,
} from '../helpers';
import {
  adjustSettingsSubtasks,
  composeYourMessagingSubtasks,
  createCareersSiteSubtasks,
  getStartedSubtasks,
  GettingStartedAbilitiesMap,
  GettingStartedNoAbilitiesMap,
  manageCandidatesSubtasks,
  postYourFirstJobSubtasks,
  viewJobsSubtasks,
  visitOurMarketplaceSubtasks,
} from '../types';

const { actions, selectors, reducer, namespace } =
  createSingleEntityStore<GettingStartedConfigWithContent>()('getting-started-config', {
    customActions: _adapter => ({
      triggerFirstTimeDrawerCloseTooltip: ngrxEvent<void>(),
      forceFirstTimeDrawerCloseTooltipClose: ngrxEvent<void>(),
      triggerDismissGuideCloseTooltip: ngrxEvent<void>(),
      forceDismissGuideCloseTooltipClose: ngrxEvent<void>(),

      toggleAcademyButton: (state, toggled: boolean) => _adapter.markAsPending(state),
      toggleAcademyButtonSuccess: (state, payload) =>
        pipeState(
          state,
          next => _adapter.markAsLoaded(next),
          next => _adapter.update(next, payload),
        ),
      toggleAcademyButtonFailed: (state, error: HttpErrorResponse) =>
        _adapter.setError(state, error),
    }),
  });

const showAcademyButtonSelector = createSelector(
  selectors.data,
  (data: GettingStartedConfigWithContent): boolean => data?.showAcademyButton,
);

const contentSelector = createSelector(
  selectors.data,
  (data: GettingStartedConfigWithContent): GettingStartedContentResponse | null =>
    data?.content ?? null,
);

const availableSubtasksSelector = createSelector(
  selectors.data,
  AbilitiesSelectors.abilityMap,
  (data: GettingStartedConfigWithContent, abilities): Partial<GettingStartedSubtaskType> =>
    Object.entries(data?.gettingStartedSteps || {}).reduce((acc, [key, value]) => {
      const requiredStepAbilities = GettingStartedAbilitiesMap[<GettingStartedSubtask>key];
      const hasAllAbilities = requiredStepAbilities.every(
        requiredAbility => abilities[requiredAbility],
      );
      const noAbilities =
        GettingStartedNoAbilitiesMap[<keyof typeof GettingStartedNoAbilitiesMap>key];
      const hasNoAbilities = noAbilities?.some(noAbility => !abilities[noAbility]) ?? true;

      return (hasAllAbilities && hasNoAbilities) || (!hasAllAbilities && !hasNoAbilities)
        ? { ...acc, [key]: value }
        : acc;
    }, {}),
);

const percentageProgressSelector = createSelector(
  availableSubtasksSelector,
  (data: GettingStartedSubtaskType): number => {
    if (!data) return 0;
    const steps = Object.values(data);
    const completedSteps = steps.filter(Boolean).length;
    return Math.round((completedSteps / steps.length) * 100);
  },
);

const getStartedSubtasksSelector = createSelector(
  availableSubtasksSelector,
  (data: GettingStartedSubtaskType): GetStartedSubtasks | {} =>
    getAvailableSubtasksVisitedState(getStartedSubtasks, data),
);

const adjustSettingsSubtasksSelector = createSelector(
  availableSubtasksSelector,
  (data: GettingStartedSubtaskType): AdjustSettingsSubtasks | {} =>
    getAvailableSubtasksVisitedState(adjustSettingsSubtasks, data),
);

const createCareersSiteSubtasksSelector = createSelector(
  availableSubtasksSelector,
  (data: GettingStartedSubtaskType): CreateCareersSiteSubtasks | {} =>
    getAvailableSubtasksVisitedState(createCareersSiteSubtasks, data),
);

const postYourFirstJobSubtasksSelector = createSelector(
  availableSubtasksSelector,
  (data: GettingStartedSubtaskType): PostYourFirstJobSubtasks | {} =>
    getAvailableSubtasksVisitedState(postYourFirstJobSubtasks, data),
);

const manageCandidatesSubtasksSelector = createSelector(
  availableSubtasksSelector,
  (data: GettingStartedSubtaskType): ManageCandidatesSubtasks | {} =>
    getAvailableSubtasksVisitedState(manageCandidatesSubtasks, data),
);

const visitOurMarketplaceSubtasksSelector = createSelector(
  availableSubtasksSelector,
  (data: GettingStartedSubtaskType): VisitOurMarketplaceSubtasks | {} =>
    getAvailableSubtasksVisitedState(visitOurMarketplaceSubtasks, data),
);

const viewJobsSubtasksSelector = createSelector(
  availableSubtasksSelector,
  (data: GettingStartedSubtaskType): ViewJobsSubtasks | {} =>
    getAvailableSubtasksVisitedState(viewJobsSubtasks, data),
);

const composeYourMessagingSubtasksSelector = createSelector(
  availableSubtasksSelector,
  (data: GettingStartedSubtaskType): ComposeYourMessagingSubtasks | {} =>
    getAvailableSubtasksVisitedState(composeYourMessagingSubtasks, data),
);

const firstUnfinishedSubtaskSelector = createSelector(
  availableSubtasksSelector,
  (data: GettingStartedSubtaskType): FirstUnfinishedStepAndSubtask => {
    if (!data) return { step: null, subtask: null };
    const subtask: GettingStartedSubtask | null = firstUnfinishedSubtask(data);
    const step: GettingStartedStep | null = findStepForSubtask(subtask!);
    return { step, subtask };
  },
);

const GettingStartedSelectors = {
  ...selectors,
  showAcademyButtonSelector,
  contentSelector,
  availableSubtasksSelector,
  percentageProgressSelector,
  getStartedSubtasksSelector,
  adjustSettingsSubtasksSelector,
  createCareersSiteSubtasksSelector,
  postYourFirstJobSubtasksSelector,
  manageCandidatesSubtasksSelector,
  visitOurMarketplaceSubtasksSelector,
  firstUnfinishedSubtaskSelector,
  viewJobsSubtasksSelector,
  composeYourMessagingSubtasksSelector,
};

export {
  actions as GettingStartedActions,
  GettingStartedSelectors,
  reducer as GettingStartedReducer,
  namespace as GettingStartedNamespace,
};
